import { useCallback, useMemo } from 'react';
import { getIn } from 'final-form';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmPmFormAct } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormAct';
import { IDecisionActForm } from 'DecisionPackage/MainModule/interfaces/IDecisionActForm';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import styles from './ErknmActSignInfo.module.scss';
import { ProfKindCodesEnum } from '../../../../../../APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { ExternalSystemSignInfoBlock } from '../../../../../../CorePackage/MainModule/components/ExternalSystemSignInfoBlock';

interface IErknmActSignInfoProps {
  id: string;
  name: string;
  isPm?: boolean;
  isDecision?: boolean;
  isDisabledField: boolean;
  inspectors: IKnmErknmFormInspector[] | undefined;
  value: IKnmErknmFormAct | IKnmPmFormAct | IDecisionActForm;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
}

export const ErknmActSignInfo = ({
  id,
  isPm,
  formRef,
  name,
  isDisabledField,
  inspectors,
  isDecision,
  value,
}: IErknmActSignInfoProps) => {
  /**
   * Список вариантов для выбора должностного лица, подписавшего акт
   */
  const inspectorsErknmOptions = useMemo(() => {
    return (
      inspectors?.map(inspector => {
        return {
          id: inspector.guid || '',
          value: inspector,
          label: `${inspector.fullName} - ${inspector.position?.label}`,
        };
      }) || []
    );
  }, [inspectors]);

  /**
   * Функция изменения подписанта.
   * Если подписанта нет в knoInspectors внутри акта, то добавляем его туда
   */
  const handleFioAndPositionSingerChange = useCallback(
    (v: { id: string; value: IKnmErknmFormInspector; label: string } | undefined) => {
      if (v) {
        const newKnoInspector: IKnmErknmFormInspector = {
          knoInspector: v,
          ...v.value,
        };

        if (value?.knoInspectors) {
          if (
            !value.knoInspectors.find(
              inspector =>
                inspector.knoInspector?.value.listId === v.value.listId ||
                inspector.knoInspector?.value.fullName === v.value.fullName,
            )
          ) {
            // @ts-ignore
            formRef?.current?.change(`${name}.knoInspectors`, [...value.knoInspectors, newKnoInspector]);
          }
        } else {
          // @ts-ignore
          formRef?.current?.change(`${name}.knoInspectors`, [newKnoInspector]);
        }
      }
    },
    [formRef, name, value],
  );

  const hintText = useMemo(() => {
    if (isPm) {
      return (
        'Выбрать инспектора из раздела «Сведения о должностных лицах контрольных (надзорных) органов, ' +
        'участвующих в соответствующих профилактических мероприятиях»'
      );
    }

    if (isDecision) {
      return 'Выбрать инспектора из раздела «Сведения о должностных лицах, участвующих в КНМ, СР»';
    }

    return 'Выбрать инспектора из раздела «Сведения о должностных лицах, участвующих в КНМ»';
  }, [isPm, isDecision]);

  // @ts-ignore
  // const formValue = formRef?.current.getFieldState(name)?.value;
  const formValue = getIn(formRef?.current.getState().values, name);
  // @ts-ignore
  const isPmPPType = isPm ? formRef?.current.getFieldState('pmType')?.value?.code === ProfKindCodesEnum.PP : false;

  let signInfoVariant;

  if ((isDecision || (!isDecision && !isPm) || (isPm && isPmPPType)) && formValue?.systemSignatureUsed) {
    signInfoVariant = (
      <ExternalSystemSignInfoBlock
        systemSignatureDateTime={formValue.systemSignatureDateTime as string}
        systemSignatureTitle={formValue.systemSignatureTitle}
      />
    );
  } else {
    signInfoVariant = (
      <>
        <FieldBlock
          id={`${id}.fioAndPositionSinger`}
          label={getTitleFieldWithRedStar({
            label: `ФИО подписавшего ${isDecision ? 'решение' : 'акт'}`,
            field: `${name}.fioAndPositionSinger`,
            isPm,
            isDecision,
            isKnm: !isPm && !isDecision,
          })}
          color="black"
          placementLabel="left"
          className={styles.FieldBlock}
          margin="bottom"
        >
          <SelectInputField
            name={`${name}.fioAndPositionSinger`}
            placeholder="Выберите должностное лицо"
            options={inspectorsErknmOptions}
            disabled={isDisabledField}
            onChange={handleFioAndPositionSingerChange}
          />
        </FieldBlock>
        <Hint text={hintText} />
      </>
    );
  }

  return (
    <FieldBlock label={`Подпись ${isDecision ? 'решения' : 'акта'}`} className={styles.GroupMarginBottom}>
      <Row>{signInfoVariant}</Row>
    </FieldBlock>
  );
};
